import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWatchListenFeed, useIsMobile } from '../../hooks/useReaderCorner';
import { supabase } from '../../lib/supabase';

const C = { rust: '#B85C38', forest: '#2D4A3E', cream: '#F5F0E8', gold: '#d4a84b', grey: '#8B7355', ink: '#292524', red: '#dc2626', purple: '#7c3aed' };

const TYPE_META = {
  youtube:  { icon: '🎬', label: 'Video',    color: C.red },
  podcast:  { icon: '🎙️', label: 'Podcast',  color: C.purple },
  showcase: { icon: '🎨', label: 'Showcase', color: C.gold },
};

function Avatar({ username, avatar_url, size = 26 }) {
  const navigate = useNavigate();
  const initial = (username || '?').slice(0, 1).toUpperCase();
  return (
    <button
      onClick={() => username && navigate(`/u/${username}`)}
      style={{
        width: size, height: size, borderRadius: 'var(--radius-circle)', flexShrink: 0,
        background: avatar_url ? `url(${avatar_url}) center/cover` : C.rust,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.42, fontWeight: 700, color: 'var(--brand-white)', fontFamily: 'var(--font-display)',
        border: `2px solid ${C.gold}`, cursor: username ? 'pointer' : 'default', padding: 'var(--space-none)',
      }}
    >
      {!avatar_url && initial}
    </button>
  );
}

function Handle({ username }) {
  const navigate = useNavigate();
  if (!username) return null;
  return (
    <button onClick={() => navigate(`/u/${username}`)}
      style={{ background: 'none', border: 'none', padding: 'var(--space-none)', fontSize: 12.5, color: C.forest, fontWeight: 700, cursor: 'pointer' }}>
      @{username}
    </button>
  );
}

// ── Featured panel — "now showing" ────────────────────────────────────────────
function FeaturedPanel({ item, isMobile }) {
  const audioRef = useRef(null);
  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [curTime, setCurTime]   = useState('0:00');

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
    setCurTime('0:00');
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [item?.id]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    function onTime() {
      const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
      setProgress(pct);
      const m = Math.floor(audio.currentTime / 60);
      const s = Math.floor(audio.currentTime % 60).toString().padStart(2, '0');
      setCurTime(`${m}:${s}`);
    }
    function onEnd() { setPlaying(false); setProgress(0); setCurTime('0:00'); }
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => { audio.removeEventListener('timeupdate', onTime); audio.removeEventListener('ended', onEnd); };
  }, [item?.id]);

  function togglePlay() {
    if (!item?.audio_url) { if (item?.url) window.open(item.url, '_blank'); return; }
    if (playing) { audioRef.current?.pause(); setPlaying(false); }
    else {
      if (audioRef.current.src !== item.audio_url) audioRef.current.src = item.audio_url;
      audioRef.current.play().catch(() => window.open(item.url, '_blank'));
      setPlaying(true);
    }
  }

  if (!item) {
    return (
      <div style={{ background: 'var(--brand-white)', border: `1px solid ${C.gold}55`, borderRadius: 'var(--radius-xl)', padding: 'var(--space-h4)', textAlign: 'center' }}>
        <p style={{ fontSize: 'var(--font-size-base)', color: C.grey, fontStyle: 'italic', margin: 'var(--space-none)' }}>Nothing here yet.</p>
      </div>
    );
  }

  const meta = TYPE_META[item.type];

  return (
    <div style={{ background: 'var(--brand-white)', border: `1px solid ${C.gold}55`, borderRadius: 'var(--radius-xl)', overflow: 'hidden', boxShadow: '0 8px 28px rgba(45,74,62,0.08)' }}>
      <div style={{ padding: '10px 18px', borderBottom: `1px solid ${C.gold}33`, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: meta.color }}>
          {meta.icon} Now Showing · {meta.label}
        </span>
      </div>

      {item.type === 'youtube' && (
        <div>
          <div style={{ width: '100%', aspectRatio: '16/9', background: 'var(--brand-black)' }}>
            <iframe
              key={item.id}
              src={`https://www.youtube.com/embed/${item.video_id}?rel=0`}
              title={item.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          </div>
          <p style={{ fontSize: 10.5, color: C.grey, textAlign: 'center', margin: '8px 0 0' }}>
            Video not loading?{' '}
            <a href={`https://www.youtube.com/watch?v=${item.video_id}`} target="_blank" rel="noopener noreferrer" style={{ color: C.red, fontWeight: 700, textDecoration: 'none' }}>
              Watch on YouTube ↗
            </a>
          </p>
        </div>
      )}

      {item.type === 'podcast' && (
        <div style={{ padding: isMobile ? '24px 18px' : '32px 28px', textAlign: 'center' }}>
          <audio ref={audioRef} style={{ display: 'none' }} />
          <div style={{ width: 72, height: 72, borderRadius: 'var(--radius-xl)', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h2)', margin: '0 auto 16px' }}>
            🎙️
          </div>
          <button onClick={togglePlay}
            style={{ width: 56, height: 56, borderRadius: 'var(--radius-circle)', background: playing ? C.purple : 'var(--neutral-100)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', transition: 'background 0.15s' }}>
            {playing
              ? <div style={{ display: 'flex', gap: 'var(--space-xs)' }}><div style={{ width: 5, height: 20, background: 'var(--brand-white)', borderRadius: 2 }} /><div style={{ width: 5, height: 20, background: 'var(--brand-white)', borderRadius: 2 }} /></div>
              : <div style={{ width: 0, height: 0, borderLeft: `16px solid ${C.purple}`, borderTop: '10px solid transparent', borderBottom: '10px solid transparent', marginLeft: 'var(--space-xs)' }} />}
          </button>
          {item.audio_url && (
            <div style={{ maxWidth: 320, margin: '0 auto' }}>
              <div style={{ height: 4, borderRadius: 2, background: '#f0ede8', cursor: 'pointer' }}
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pct = (e.clientX - rect.left) / rect.width;
                  if (audioRef.current?.duration) audioRef.current.currentTime = pct * audioRef.current.duration;
                }}>
                <div style={{ height: '100%', width: `${progress}%`, background: C.purple, borderRadius: 2, transition: 'width 0.4s linear' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-xs)' }}>
                <span style={{ fontSize: 9.5, color: C.grey }}>{curTime}</span>
                <span style={{ fontSize: 9.5, color: C.grey }}>{item.duration || ''}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {item.type === 'showcase' && (
        <div style={{ padding: isMobile ? '20px' : '28px', background: '#faf7f0' }}>
          <div style={{ background: 'var(--brand-white)', padding: 'var(--space-10)', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', border: '1px solid #eee', maxWidth: 480, margin: '0 auto' }}>
            <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', background: 'var(--neutral-100)' }}>
              <img src={item.image_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ padding: '10px 4px 4px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-base)', fontStyle: 'italic', color: C.ink, margin: 'var(--space-none)' }}>{item.title}</p>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.gold}22` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: item.type !== 'showcase' ? 6 : 0 }}>
          <Avatar username={item.username} avatar_url={item.avatar_url} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Handle username={item.username} />
            {item.type === 'podcast' && <p style={{ fontSize: 'var(--font-size-xxs)', color: C.grey, margin: '1px 0 0' }}>{item.podcast_name}</p>}
          </div>
        </div>
        {item.type !== 'showcase' && (
          <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: C.ink, margin: 'var(--space-none)', lineHeight: 1.35 }}>{item.title}</p>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-14)', marginTop: 'var(--space-sm)' }}>
          {item.type === 'youtube' && item.channel_url && (
            <a href={item.channel_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--font-size-xs)', color: C.red, fontWeight: 700, textDecoration: 'none' }}>View full channel →</a>
          )}
          {item.type === 'podcast' && (item.spotify_url || item.apple_url) && (
            <a href={(item.spotify_url || item.apple_url).startsWith('http') ? (item.spotify_url || item.apple_url) : `https://${item.spotify_url || item.apple_url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--font-size-xs)', color: C.purple, fontWeight: 700, textDecoration: 'none' }}>Listen on Spotify / Apple →</a>
          )}
          {item.type === 'showcase' && item.link && (
            <a href={item.link.startsWith('http') ? item.link : `https://${item.link}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--font-size-xs)', color: C.gold, fontWeight: 700, textDecoration: 'none' }}>View full work →</a>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Guide list row ────────────────────────────────────────────────────────────
function GuideRow({ item, active, onClick }) {
  const meta = TYPE_META[item.type];
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-10)', width: '100%', textAlign: 'left',
      padding: '9px 10px', borderRadius: 'var(--radius-10)', border: active ? `1.5px solid ${meta.color}` : '1px solid transparent',
      background: active ? `${meta.color}14` : 'transparent', cursor: 'pointer',
    }}>
      <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', overflow: 'hidden', flexShrink: 0, background: '#f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-lg)' }}>
        {item.type === 'youtube' && item.thumbnail_url && (
          <img src={item.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.textContent = '🎬'; }} />
        )}
        {item.type === 'showcase' && item.image_url && (
          <img src={item.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.textContent = '🎨'; }} />
        )}
        {item.type === 'podcast' && meta.icon}
        {item.type === 'youtube' && !item.thumbnail_url && meta.icon}
        {item.type === 'showcase' && !item.image_url && meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 11.5, fontWeight: 700, color: C.ink, margin: 'var(--space-none)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</p>
        <p style={{ fontSize: 'var(--font-size-xxs)', color: C.grey, margin: '2px 0 0' }}>{meta.icon} @{item.username || '—'}</p>
      </div>
    </button>
  );
}

// Handles two possible shapes coming out of the hook: the current flat shape
// (one row per video/episode/showcase piece, with its own top-level id), and
// an older grouped-by-creator shape (a card per creator with videos[]/
// episodes[] arrays inside, no top-level id). Whichever one is actually live,
// this always produces flat, uniquely-identified items the rest of the tab
// can rely on.
function normalizeItems(raw) {
  const flat = [];
  (raw || []).forEach(entry => {
    const alreadyFlat = entry.id && (entry.video_id !== undefined || entry.audio_url !== undefined || entry.image_url !== undefined);
    if (alreadyFlat) { flat.push(entry); return; }

    const creator = { user_id: entry.user_id, username: entry.username, avatar_url: entry.avatar_url };

    if (entry.type === 'youtube' && Array.isArray(entry.videos)) {
      entry.videos.forEach((v, i) => {
        flat.push({
          ...creator, type: 'youtube', id: `yt-${entry.user_id}-${v.video_id || i}`,
          title: v.title || 'YouTube Video', video_id: v.video_id, thumbnail_url: v.thumbnail_url,
          channel_url: entry.channel_url || null,
        });
      });
    } else if (entry.type === 'podcast' && Array.isArray(entry.episodes)) {
      entry.episodes.forEach((ep, i) => {
        flat.push({
          ...creator, type: 'podcast', id: `pod-${entry.user_id}-${i}`,
          title: ep.title || 'Episode', podcast_name: entry.podcast_name || 'Podcast',
          audio_url: ep.audio_url || null, url: ep.url || null,
          duration: ep.duration || null, date: ep.date || null,
          spotify_url: entry.spotify_url || null, apple_url: entry.apple_url || null,
        });
      });
    }
    // Old shape never carried showcase data at all, so there's nothing to
    // expand for that case — it'll only appear once the live hook is current.
  });
  return flat;
}

export default function WatchListenTab() {
  const { items: rawItems, loading } = useWatchListenFeed();
  const [showcaseItems, setShowcaseItems] = useState([]);
  const [showcaseLoading, setShowcaseLoading] = useState(true);
  const [filter, setFilter]     = useState('all');
  const [featuredId, setFeaturedId] = useState(null);
  const isMobile = useIsMobile();

  // Fetched independently of useWatchListenFeed — self-contained here so
  // Showcase works regardless of which version of that hook is actually
  // deployed, since showcase_items was never part of the older shape at all.
  useEffect(() => {
    (async () => {
      setShowcaseLoading(true);
      try {
        const { data: profiles, error } = await supabase
          .from('user_profiles')
          .select('user_id, username, avatar_url, showcase_items')
          .not('showcase_items', 'is', null);
        if (error) throw error;

        const flat = [];
        (profiles || []).forEach(p => {
          (p.showcase_items || []).slice(0, 5).forEach((item, i) => {
            flat.push({
              user_id: p.user_id, username: p.username, avatar_url: p.avatar_url,
              type: 'showcase', id: `show-${p.user_id}-${i}`,
              title: item.title || 'Showcase piece', image_url: item.url, link: item.link || null,
            });
          });
        });
        setShowcaseItems(flat);
      } catch (err) {
        console.error('Showcase fetch error:', err.message);
        setShowcaseItems([]);
      } finally {
        setShowcaseLoading(false);
      }
    })();
  }, []);

  const items = [...normalizeItems(rawItems), ...showcaseItems];

  useEffect(() => {
    if (!featuredId && items.length > 0) setFeaturedId(items[0].id);
  }, [items]);

  const featured = items.find(i => i.id === featuredId) || null;
  const visibleList = filter === 'all' ? items : items.filter(i =>
    filter === 'videos' ? i.type === 'youtube' :
    filter === 'podcasts' ? i.type === 'podcast' :
    i.type === 'showcase'
  );

  function chip(active) {
    return {
      padding: isMobile ? '6px 12px' : '7px 15px', borderRadius: 'var(--radius-pill)', fontSize: isMobile ? 10.5 : 11.5, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer',
      background: active ? C.gold : 'transparent', color: C.forest,
      border: active ? `1px solid ${C.gold}` : `1px solid ${C.forest}44`,
      whiteSpace: 'nowrap', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
    };
  }

  if ((loading || showcaseLoading) && items.length === 0) {
    return <p style={{ textAlign: 'center', color: C.grey, fontSize: 'var(--font-size-base)' }}>Loading…</p>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.6fr 1fr', gap: isMobile ? 16 : 20, alignItems: 'start' }}>
      <div>
        <FeaturedPanel item={featured} isMobile={isMobile} />
      </div>

      <div style={{ background: 'var(--brand-white)', border: `1px solid ${C.gold}55`, borderRadius: 'var(--radius-xl)', padding: 'var(--space-14)', maxHeight: isMobile ? 360 : 520, display: 'flex', flexDirection: 'column' }}>
        <div className="rc-hide-scrollbar" style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 18, paddingBottom: 'var(--space-14)', overflowX: 'auto', borderBottom: `1px solid ${C.gold}33` }}>
          <button onClick={() => setFilter('all')} style={chip(filter === 'all')}>All</button>
          <button onClick={() => setFilter('videos')} style={chip(filter === 'videos')}>🎬 Videos</button>
          <button onClick={() => setFilter('podcasts')} style={chip(filter === 'podcasts')}>🎙️ Podcasts</button>
          <button onClick={() => setFilter('showcase')} style={chip(filter === 'showcase')}>🎨 Showcase</button>
        </div>

        {visibleList.length === 0 && (
          <p style={{ fontSize: 'var(--font-size-sm)', color: C.grey, fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>Nothing here yet.</p>
        )}

        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {visibleList.map(item => (
            <GuideRow key={item.id} item={item} active={item.id === featuredId} onClick={() => setFeaturedId(item.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}